import { showHttpError, showSuccess } from '@calendly/alert'
import { httpRequest } from '@calendly/http-tools'
import { I18n } from '@calendly/i18n'
import { createSlice } from '@reduxjs/toolkit'
import moment from 'moment-timezone'

import { orderBySelector, sortSelector, pageSelector } from './selectors'

const initialState = {
  inviteeDeletionStatus: '',
  eventsDeletionStatus: '',
  isDeletionRequestsLoading: true,
  deletionRequests: { collection: [], pagination: null },
  orderBy: 'created_at',
  sort: 'desc',
  page: 1
}

const slice = createSlice({
  name: 'dataDeletion',
  initialState,
  reducers: {
    reset: () => initialState,
    setOrderBy: (state, { payload }) => {
      state.orderBy = payload
    },
    setSort: (state, { payload }) => {
      state.sort = payload
    },
    setPage: (state, { payload }) => {
      state.page = payload
    },
    setInviteeDeletionStatus: (state, { payload }) => {
      state.inviteeDeletionStatus = payload
    },
    setEventsDeletionStatus: (state, { payload }) => {
      state.eventsDeletionStatus = payload
    },
    setIsDeletionRequestsLoading: (state, { payload }) => {
      state.isDeletionRequestsLoading = payload
    },
    setDeletionRequests: (state, { payload }) => {
      state.deletionRequests = payload
    }
  }
})

// i18n-tasks-use t('publisher.data_requests.notice.account_deleted')
// i18n-tasks-use t('publisher.data_requests.notice.invitees_deleted')
// i18n-tasks-use t('publisher.data_requests.notice.events_deleted')
// i18n-tasks-use t('publisher.organization_settings.data_deletion.notice.cancelation')
const flash = (dispatch, flashNotice) => {
  const text = I18n.t(`publisher.organization_settings.data_deletion.notice.${flashNotice}`, {
    defaults: [
      {
        message: I18n.t(`publisher.data_requests.notice.${flashNotice}`)
      }
    ]
  })
  dispatch(showSuccess(text))
}

export const loadDeletionRequests = () => (
  async (dispatch, getState) => {
    const state = getState()
    const orderBy = orderBySelector(state)
    const sort = sortSelector(state)
    const page = pageSelector(state)

    dispatch(slice.actions.setIsDeletionRequestsLoading(true))

    const [success, data] = await httpRequest({
      url: '/api/organization/data_deletion/deletion_requests',
      params: { order_by: orderBy, sort, page }
    })

    dispatch(slice.actions.setIsDeletionRequestsLoading(false))

    if (success) {
      dispatch(slice.actions.setDeletionRequests(data))
    } else {
      dispatch(showHttpError(data))
    }
  }
)

export const deleteInvitees = (inviteeList) => (
  async (dispatch) => {
    dispatch(slice.actions.setInviteeDeletionStatus(''))

    const [success, data] = await httpRequest({
      url: '/api/organization/data_deletion/invitee',
      method: 'POST',
      data: { emails: inviteeList.map((list) => list.email) }
    })

    if (success) {
      flash(dispatch, 'invitees_deleted')
      dispatch(slice.actions.setInviteeDeletionStatus('success'))
      dispatch(loadDeletionRequests())
    } else {
      dispatch(slice.actions.setInviteeDeletionStatus('error'))
      dispatch(showHttpError(data))
    }
  }
)

/**
 * @param {string} start
 * @param {string} end
 */
export const deleteEvents = (start, end) => (
  async (dispatch) => {
    dispatch(slice.actions.setEventsDeletionStatus(''))

    const [success, data] = await httpRequest({
      url: '/api/organization/data_deletion/events',
      method: 'POST',
      data: {
        start: moment.tz(start, 'UTC').startOf('day'),
        end: moment.tz(end, 'UTC').endOf('day')
      }
    })

    if (success) {
      flash(dispatch, 'events_deleted')
      dispatch(slice.actions.setEventsDeletionStatus('success'))
      dispatch(loadDeletionRequests())
    } else {
      dispatch(slice.actions.setEventsDeletionStatus('error'))
      dispatch(showHttpError(data))
    }
  }
)


export const cancelRequest = ({ request }) => (
  async (dispatch) => {
    const [success, data] = await httpRequest({
      url: `/api/organization/data_deletion/cancel/${request.id}`,
      method: 'PATCH'
    })


    if (success) {
      flash(dispatch, 'cancelation')
      dispatch(loadDeletionRequests())
    } else {
      dispatch(showHttpError(data))
    }
  }
)

export const { reducer } = slice
export const { setOrderBy, setSort, setPage, reset } = slice.actions
